import { useEffect, useState } from 'react';
import { Textarea } from '../Components/UI/textarea';
import { toast } from 'sonner';
import { getFdbkScoreText, sentimentEmoji } from '../Utils/Utilities';
import { getConnFeedbackDetails, manageFeedbackSetAPI } from './SentimentAPI';
import { isSameDay } from 'date-fns';
import { initializeMetaPixel, trackMetaPixelEvent } from '../Hooks/useMetaPixel';

const Sentiment = () => {
  const [hoverRating, setHoverRating] = useState(0);
  const [review, setReview] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agencyInfo, setAgencyInfo] = useState(null);
  const [connInfo, setConnInfo] = useState(null);

  const searchParams = new URLSearchParams(window.location.search);
  const agencyID = searchParams.get("aid");
  const connID = searchParams.get("cid");
  const defaultRating = Number(searchParams.get("rating")) || 0;

  const [rating, setRating] = useState(defaultRating);

  // Track when this Sentiment page is opened
  useEffect(() => {
    initializeMetaPixel();
    trackMetaPixelEvent('SentimentPageOpened');
  }, []);


  const ratings = [
    { 
      label: 'Very Unhappy', 
      color: 'sahi-red',
      question: "Uh oh! Please help us correct!"
    },
    { 
      label: 'Somewhat Unhappy', 
      color: 'sahi-orange',
      question: "What went wrong? Help us improve!"
    },
    { 
      label: 'Neutral', 
      color: 'sahi-dark-blue',
      question: "How can we make it better?"
    },
    { 
      label: 'Somewhat Happy', 
      color: 'sahi-blue',
      question: "How can we do even better?"
    },
    { 
      label: 'Very Happy', 
      color: 'sahi-green',
      question: "What did you love most about our service?"
    }
  ];

  useEffect(() => {
    const fetchConnDetails = async () => {
      try {
        const result = await getConnFeedbackDetails({ agencyID, connID }, "FDBK");

        if (result?.error === false && result.parsedDataSet) {
          const { Table1, Table2 } = result.parsedDataSet;
          const agencyData = Table1?.[0] || {};
          const connData = Table2?.[0] || {};

          setAgencyInfo(agencyData);
          setConnInfo(connData);

          // Check if both dates are from today
          const today = new Date();
          const lastFdbkReqDT = connData?.LastFdbkReqDT ? new Date(connData.LastFdbkReqDT) : null;
          const fdbkDT = connData?.FdbkDT ? new Date(connData.FdbkDT) : null;

          if (lastFdbkReqDT && fdbkDT && isSameDay(lastFdbkReqDT, today) && isSameDay(fdbkDT, today)) {
            setSubmitted(true);
          }
        } else {
          toast.error(result?.message || "Failed to fetch details");
        }
      } catch (err) {
        console.error(err);
        toast.error("Error fetching contact details");
      }
    };

    fetchConnDetails();
  }, []);

  const handleEmojiClick = (emojiIndex) => {
    if (!isSubmitting && !submitted) {
      setRating(emojiIndex);
    }
  };

  const handleEmojiHover = (emojiIndex) => {
    if (!isSubmitting && !submitted) {
      setHoverRating(emojiIndex);
    }
  };

  const handleEmojiLeave = () => {
    if (!isSubmitting && !submitted) {
      setHoverRating(0);
    }
  };

  const handleSubmit = async () => {
    if (rating === 0) {
      toast.error('Please select a rating.');
      return;
    } else if (review === '') {
      toast.error("Please enter a feedback notes.");
      return;
    }

    setIsSubmitting(true);
    try {
      const feedbackData = {
        agencyID,
        agencyName: agencyInfo?.AgencyName,
        agencyEmail: agencyInfo?.AgencyEmail,
        connID,
        contactPerson: connInfo.ContactName,
        bizName: connInfo.BizName,
        connEmail: connInfo.Email,
        connMobile: connInfo.Mobile,
        fdbkScore: rating,
        fdbkNotes: review || "",
        fdbkTxt: getFdbkScoreText(rating),
      };

      const result = await manageFeedbackSetAPI(feedbackData);
      
      if (result.error === false) {
        setSubmitted(true);
        toast.success('Feedback Submitted.');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCurrentQuestion = () => {
    if (rating > 0) {
      return ratings[rating - 1].question;
    }
    return null;
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg md:p-8 p-6 w-full max-w-2xl text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-sahi-green" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
            </svg>
          </div>
          <h2 className="md:text-4xl text-2xl font-semibold text-gray-800 mb-2">Thank You!</h2>
          <p className="text-gray-600 mb-4 text-sm md:text-base">Your review has been submitted.</p>
          <p className="text-gray-500 text-sm">We truly appreciate your feedback!</p>
        </div>
      </div>
    );
  }

  return (
    <div className="md:min-h-screen h-full bg-gray-100 flex items-center justify-center md:p-4">
      <div className="bg-white rounded-lg md:shadow-lg p-8 md:px-8 px-6 w-full max-w-2xl md:border">
        <div className="text-center mb-6">
          <h1 className="md:text-4xl text-3xl font-semibold text-sahi-primary-color mb-2">Feedback</h1>
          <p className="text-gray-600 md:text-base text-xs">Please help us become your favourite supplier!</p>
        </div>

        <div className="mb-6">
          <h3 className="md:text-xl text-lg font-semibold text-gray-800 mb-2">{agencyInfo?.AgencyName}</h3>
          <p className="text-xs text-gray-400 mb-4 block md:hidden">
            We are committed to delivering exceptional products and services. Your review helps us maintain the highest standards.
          </p>
          <p className="md:text-sm text-xs text-gray-400 mb-4 hidden md:block">
            We are committed to delivering exceptional products and services. Your review helps us maintain the highest standards and continue to exceed your expectations.
          </p>
        </div>

        <div className="mb-6">
          <p className="text-gray-700 font-medium mb-4 text-left md:text-base text-sm">
            How do you feel about working with us?
          </p>
          
          <div className="flex lg:flex-row flex-col items-center lg:space-x-4 mb-6">
            <div className="flex lg:space-x-2">
              {[1, 2, 3, 4, 5].map((emojiIndex) => (
                <button
                  key={emojiIndex}
                  onClick={() => handleEmojiClick(emojiIndex)}
                  onMouseEnter={() => handleEmojiHover(emojiIndex)}
                  onMouseLeave={handleEmojiLeave}
                  className="transition-all duration-200 focus:outline-none p-2 rounded-full hover:bg-gray-100"
                  disabled={isSubmitting || submitted}
                >
                  <div
                    className={`
                      h-10 w-10 sm:h-12 sm:w-12 flex items-center justify-center rounded-full border-2 transition-all duration-200
                      ${emojiIndex <= (hoverRating || rating)
                        ? `bg-${ratings[(hoverRating || rating) - 1]?.color} border-white shadow-lg scale-110`
                        : 'border-gray-300 bg-white'
                      }
                      hover:scale-110
                      ${(isSubmitting || submitted) ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {emojiIndex <= (hoverRating || rating) ? sentimentEmoji(emojiIndex, '!text-white') : sentimentEmoji(emojiIndex, '!text-sahi-grey')}
                  </div>
                </button>
              ))}
            </div>

            {(rating > 0 || hoverRating > 0) && (
              <div>
                <span className={`md:text-lg text-base font-medium text-${ratings[(hoverRating || rating) - 1]?.color}`}>
                  {ratings[(hoverRating || rating) - 1].label}
                </span>
              </div>
            )}
          </div>
        </div>

        {getCurrentQuestion() && (
          <div className="mb-4">
            <p className="text-gray-700 font-medium text-left md:text-base text-sm">
              {getCurrentQuestion()}
            </p>
          </div>
        )}

        <div className="mb-6">
          <Textarea
            value={review}
            onChange={(e) => setReview(e.target.value)}
            placeholder="Enter your review"
            className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md"
            rows={4}
            disabled={isSubmitting || submitted}
          />
        </div>

        <button
          onClick={handleSubmit}
          disabled={isSubmitting || rating === 0 || submitted || review === ''}
          className={`w-full font-medium py-3 px-4 rounded-md transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center ${
            (isSubmitting || rating === 0 || submitted || review === '')
              ? 'bg-gray-400 text-gray-600 cursor-not-allowed'
              : `bg-${ratings[rating - 1]?.color} text-white hover:bg-${ratings[rating - 1]?.color}/90`
          }`}
        >
          {isSubmitting ? (
            <>
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-gray-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Submitting...
            </>
          ) : submitted ? (
            <>
              <span className="mr-2">✓</span>
              Feedback Already Submitted
            </>
          ) : (
            <>
              <span className="mr-2">✓</span>
              Submit Feedback
            </>
          )}
        </button>

        <div className="text-center mt-6">
          <p className="text-sm text-gray-500">
            Powered by{' '}
            <a href="https://sahicrm.com/" className="text-sahi-green hover:underline">
              SahiClub.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sentiment;